import { Link, useLocation } from "react-router-dom";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import api from "@/lib/api";

const DEFAULT_EXTERNAL = "https://www.bookmytutor.com/download";

// Resolves the active tutor-registration target based on site settings.
// Returns: { href, isExternal, source, target, rel, onClick }
export function useTutorCta(placement) {
  const s = useSiteSettings();
  const location = useLocation();
  const isExternal = (s?.registration_source || "website_form") === "book_my_tutor";
  const url = (s?.external_tutor_url && s.external_tutor_url.trim()) || DEFAULT_EXTERNAL;
  const href = isExternal ? url : "/become-a-tutor";

  const onClick = () => {
    // Fire-and-forget analytics; never block UX
    try {
      api.post("/track/tutor-click", {
        source: isExternal ? "book_my_tutor" : "website_form",
        page: location?.pathname || "",
        redirect_url: href,
        placement: placement || "unknown",
      }).catch(() => {});
    } catch { /* noop */ }
  };

  return {
    href,
    isExternal,
    source: isExternal ? "book_my_tutor" : "website_form",
    target: isExternal ? "_blank" : undefined,
    rel: isExternal ? "noopener noreferrer" : undefined,
    onClick,
  };
}

// Drop-in replacement for any <Link to="/become-a-tutor"> CTA.
// Props pass through (className, data-testid, children, etc.).
export default function TutorCtaLink({ placement, children, className, ...rest }) {
  const { href, isExternal, target, rel, onClick } = useTutorCta(placement);
  if (isExternal) {
    return (
      <a href={href} target={target} rel={rel} className={className} onClick={onClick} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={className} onClick={onClick} {...rest}>
      {children}
    </Link>
  );
}
